import { DSLContextMarker } from '../../constants';
import type { FormDSL } from '@commenda/types';

export const FORM_CONTEXT_MARKER = DSLContextMarker.FORM_CONTEXT;

export interface ContextSourceField {
  fieldId: string;
  label: string;
  component: string;
  outputsEntityId: boolean;
  entityType?: string;
}

export function getEntityContextKeys(entity: string): string[] {
  switch (entity) {
    case 'corporation':
      return ['corporationId'];
    case 'company':
      return ['companyId'];
    case 'director':
    case 'keyPerson':
      return ['directorId'];
    case 'keyPersonRelationship':
      return ['corporationId', 'directorId'];
    default:
      return [`${entity}Id`];
  }
}

/**
 * Context keys to show in the binding editor: schema `required` plus `idContextKey`
 * when missing. Example: keyPerson has required ["companyId"] for listing but
 * read/write of a specific person needs `directorId` from serviceConfig.idContextKey.
 */
export function getContextBindingEditorKeys(
  entitySchema:
    | {
        contextRequirements?: { required?: string[] };
        idContextKey?: string;
      }
    | null
    | undefined,
  entity: string
): string[] {
  const required = entitySchema?.contextRequirements?.required;
  const fallback = getEntityContextKeys(entity);
  const keys = required?.length ? [...required] : [...fallback];
  const idKey = entitySchema?.idContextKey;
  if (idKey && !keys.includes(idKey)) {
    keys.push(idKey);
  }
  return keys;
}

export function getContextSourceFields(
  dsl: FormDSL,
  currentFieldId: string | undefined,
  targetEntity: string
): ContextSourceField[] {
  const fields: ContextSourceField[] = [];

  // Walk fields in declaration order; stop once we reach `currentFieldId`
  // so only fields *before* it are eligible to bind context from.
  dsl.steps.some((step) =>
    step.modules.some((module) =>
      module.components.some((component) => {
        if (component.fieldId === currentFieldId) return true;

        if (component.fieldId) {
          const isEntitySelect = component.component === 'EntitySelect';
          const entityType = isEntitySelect
            ? ((component.props as Record<string, unknown>)?.entity as string)
            : undefined;
          const outputsTargetEntity =
            isEntitySelect && entityType === targetEntity;

          fields.push({
            fieldId: component.fieldId,
            label: component.label || component.fieldId,
            component: component.component,
            outputsEntityId: outputsTargetEntity,
            entityType,
          });
        }
        return false;
      })
    )
  );

  return fields;
}
